/**
 * Numeric fields that can be empty while you are typing in them.
 *
 * Every number field in the dashboard used to be bound straight to a number and coerced
 * on every keystroke, which produced two failures the type checker could not see:
 *
 *   `Math.max(0, Number(e.target.value))` — backspace the field to empty and `Number("")`
 *   is 0, so the field refills itself with "0" and the caret sits after it. Type a letter
 *   and it is `NaN`, which renders as "NaN" and propagates into `scoreSet`.
 *
 *   `if (kg >= 20 && kg <= 300) update(...)` — to get from 90 to 75 you must pass through
 *   "7", which is out of range, so the keystroke is dropped and the field appears frozen.
 *   Nothing on screen said why.
 *
 * Both come from the same mistake: treating a half-typed field as a number. So the draft
 * is a string, it is parsed rather than coerced, and a value that is out of range is
 * *shown to be wrong* instead of being silently refused. The committed value only moves
 * when the draft is actually valid, so a score is never computed from a half-typed
 * number either.
 */

import { COST_BOUNDS } from "@altrtrack/core";

export type NumberProblem = "empty" | "notANumber" | "belowMin" | "aboveMax" | "notWhole";

export interface Bounds {
  min: number;
  max: number;
  /** Reject a fraction. Reps and seconds are counts; kilograms are not. */
  integer?: boolean;
  /** What the field is called in the message. Lower case, no article: "reps", "kilograms". */
  unit: string;
}

export interface NumberCheck {
  /** The parsed value, present only when `problem` is null. */
  value: number | null;
  problem: NumberProblem | null;
  /** A whole sentence, ready to render. Null when there is nothing wrong. */
  message: string | null;
}

export function checkNumber(draft: string, bounds: Bounds): NumberCheck {
  const trimmed = draft.trim();
  const fail = (problem: NumberProblem, message: string): NumberCheck => ({
    value: null,
    problem,
    message,
  });

  if (trimmed === "") return fail("empty", `Enter the ${bounds.unit}.`);

  // `Number` accepts "0x10", "1e3" and " " as numbers; a phone keypad cannot produce any
  // of them, but a paste can, and each would sail past a bare `isNaN` check.
  if (!/^-?\d*\.?\d*$/.test(trimmed)) return fail("notANumber", `Use digits only.`);

  const value = Number(trimmed);
  if (!Number.isFinite(value)) return fail("notANumber", `Use digits only.`);
  if (bounds.integer && !Number.isInteger(value)) {
    return fail("notWhole", `${capitalise(bounds.unit)} must be a whole number.`);
  }
  if (value < bounds.min) {
    return fail("belowMin", `${capitalise(bounds.unit)} must be at least ${bounds.min}.`);
  }
  if (value > bounds.max) {
    return fail("aboveMax", `${capitalise(bounds.unit)} cannot be more than ${bounds.max}.`);
  }

  return { value, problem: null, message: null };
}

function capitalise(word: string): string {
  return word.charAt(0).toUpperCase() + word.slice(1);
}

/** Bounds shared by both places bodyweight is entered — the profile and one day's editor. */
export const BODYWEIGHT: Bounds = { min: 20, max: 300, unit: "bodyweight" };

/**
 * A day cannot be logged into the future, and a date typed as year 20 rather than 2020
 * is a typo rather than a workout. The floor is the earliest date a person could
 * plausibly be logging, not a data constraint.
 */
export function checkDate(draft: string, today: string): string | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(draft)) return "Pick a date.";
  if (Number.isNaN(Date.parse(`${draft}T00:00:00Z`))) return "That date doesn't exist.";
  if (draft > today) return "A day cannot be logged in the future.";
  if (draft < "2000-01-01") return "That date is before this log existed.";
  return null;
}

/** B-52. The same limits `isValidGymCosts` enforces, so the form cannot offer what core refuses. */
export const RUPEES: Bounds = { ...COST_BOUNDS.amountInr, integer: true, unit: "amount" };
export const MONTHS: Bounds = { ...COST_BOUNDS.months, integer: true, unit: "months" };
export const SESSIONS: Bounds = { ...COST_BOUNDS.sessions, integer: true, unit: "sessions" };
